import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import {
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  type User,
} from "firebase/auth";
import { firebaseAuth } from "./lib/firebase-app";

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Reload the Firebase user so a freshly-verified email is reflected. */
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthState>({ user: null, loading: true, refresh: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<AuthState, "refresh">>({ user: null, loading: true });
  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth(), (user) => {
      setState({ user, loading: false });
    });
    return unsub;
  }, []);
  const refresh = useCallback(async () => {
    const u = firebaseAuth().currentUser;
    if (!u) return;
    await u.reload();
    // New state object + re-read currentUser so emailVerified propagates.
    setState({ user: firebaseAuth().currentUser, loading: false });
  }, []);
  return <Ctx.Provider value={{ ...state, refresh }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  return useContext(Ctx);
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(firebaseAuth(), email, password);
}

/** Send (or resend) the email-verification link to the signed-in user. */
export async function sendVerificationEmail(): Promise<void> {
  const u = firebaseAuth().currentUser;
  if (!u) throw new Error("Not authenticated");
  await sendEmailVerification(u);
}

export async function signOut(): Promise<void> {
  await fbSignOut(firebaseAuth());
}

export async function getIdToken(): Promise<string> {
  const u = firebaseAuth().currentUser;
  if (!u) throw new Error("Not authenticated");
  return u.getIdToken();
}

export function humanizeAuthError(code: string | undefined): string | null {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Email or password is incorrect.";
    case "auth/user-disabled":
      return "This account is disabled. Contact your administrator.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Try again later.";
    case "auth/network-request-failed":
      return "Network error. Check your connection.";
    default:
      return null;
  }
}
